import { useParams, useSearchParams } from 'react-router-dom';
import {
  IMPERSONATION_HANDOFF_TOKEN,
  isImpersonationHandoffReadyMessage,
  subjectAppOrigin,
} from '@beatq/shared';
import {
  buildEndedGuard,
  formatConsentClock,
  setImpersonationBanner,
  setImpersonationEndedGuard,
} from '../../shell/adminSession.js';
import {
  caseDetailPath,
  customer360Path as shellCustomer360Path,
  merchant360Path as shellMerchant360Path,
  orderInvestigatePath,
} from '../../shell/subjectUrls.js';
import { ENTITY_360_ROUTES, type MirrorSubjectKind } from './catalog.js';
import {
  canReenterEndedImpersonation,
  defaultImpersonationExpiryIso,
  remainingSeconds,
  type ImpersonationScope,
} from './entity360Rules.js';

const CUSTOMER_ID_STORAGE_KEY = 'beatq.admin.customerId';
const BUSINESS_ID_STORAGE_KEY = 'beatq.admin.businessId';
const IMPERSONATION_SESSION_KEY = 'beatq.admin.impersonationSessionId';
const IMPERSONATION_SESSION_STATE_KEY = 'beatq.admin.impersonationSession';
const IMPERSONATION_ENDED_KEY = 'beatq.admin.impersonationEnded';

/**
 * Session fields A01 banner chrome may bind. Keep `getStoredImpersonationSessionId`
 * stable. Remaining seconds are derived from `expiresAt` via
 * `getImpersonationRemainingSeconds`.
 *
 * | Field | Read/write | A01 banner use |
 * |---|---|---|
 * | sessionId | RW | existing getter |
 * | subjectName | RW | names the subject |
 * | subjectId | RW | identifier |
 * | subjectKind | RW | customer / business |
 * | caseRef / caseId | RW | linked case |
 * | scope | RW | read vs write intensity |
 * | writeEnabled | RW | write impersonation chrome |
 * | consentRecordedAt | RW | "consent recorded HH:MM" |
 * | startedAt / expiresAt | RW | countdown; 2-minute warning |
 * | remainingSeconds | derived | `getImpersonationRemainingSeconds()` |
 */
export type StoredImpersonationSession = {
  sessionId: string;
  subjectName?: string;
  subjectId?: string;
  subjectKind?: MirrorSubjectKind | string;
  caseRef?: string;
  caseId?: string;
  scope?: ImpersonationScope;
  writeEnabled?: boolean;
  consentRecordedAt?: string;
  startedAt?: string;
  expiresAt?: string;
};

export type EndedImpersonationMarker = {
  sessionId: string;
  caseId?: string;
  subjectId?: string;
  endedAt: string;
};

/** Canonical `/customers/:id` (07-03). Legacy `/customer-360` remains a redirect. */
export function customer360Path(customerId?: string, caseId?: string): string {
  if (!customerId) return ENTITY_360_ROUTES['SCR-ADM-010'];
  return shellCustomer360Path(customerId, { caseId });
}

/** Canonical `/businesses/:id` (07-03). Legacy `/merchant-360` remains a redirect. */
export function merchant360Path(businessId?: string, caseId?: string): string {
  if (!businessId) return ENTITY_360_ROUTES['SCR-ADM-011'];
  return shellMerchant360Path(businessId, { caseId });
}

export function supportMirrorPath(
  subjectKind: MirrorSubjectKind,
  subjectId: string,
  caseId?: string,
): string {
  const base =
    subjectKind === 'customer'
      ? `/customers/${encodeURIComponent(subjectId)}/mirror`
      : `/businesses/${encodeURIComponent(subjectId)}/mirror`;
  return caseId ? `${base}?caseId=${encodeURIComponent(caseId)}` : base;
}

export function impersonationPath(
  subjectKind?: MirrorSubjectKind,
  subjectId?: string,
  caseId?: string,
  extra?: { accountId?: string },
): string {
  if (!subjectKind || !subjectId) {
    const params = new URLSearchParams();
    if (caseId) params.set('caseId', caseId);
    const query = params.toString();
    return query
      ? `${ENTITY_360_ROUTES['SCR-ADM-013']}?${query}`
      : ENTITY_360_ROUTES['SCR-ADM-013'];
  }
  const base =
    subjectKind === 'customer'
      ? `/customers/${encodeURIComponent(subjectId)}/impersonate`
      : `/businesses/${encodeURIComponent(subjectId)}/impersonate`;
  const params = new URLSearchParams();
  if (caseId) params.set('caseId', caseId);
  if (extra?.accountId) params.set('accountId', extra.accountId);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function caseReturnPath(caseId?: string | null): string {
  return caseId ? caseDetailPath(caseId) : '/case-queue';
}

export function orderInvestigationHref(orderId: string, caseId?: string): string {
  return orderInvestigatePath(orderId, { caseId });
}

export function tenantDetailHref(businessId: string): string {
  return `/tenant-detail/${encodeURIComponent(businessId)}`;
}

export function useCustomerIdParam(): string | null {
  const route = useParams();
  const [params] = useSearchParams();
  return route.customerId ?? params.get('customerId') ?? getStoredCustomerId();
}

export function useBusinessIdParam(): string | null {
  const route = useParams();
  const [params] = useSearchParams();
  return route.businessId ?? params.get('businessId') ?? getStoredBusinessId();
}

export function useCaseIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('caseId');
}

export function useMirrorSubjectParams(): {
  subjectKind: MirrorSubjectKind | null;
  subjectId: string | null;
  caseId: string | null;
  accountId: string | null;
} {
  const route = useParams();
  const [params] = useSearchParams();
  const caseId = params.get('caseId');
  const accountId = params.get('accountId');
  if (route.customerId) {
    return { subjectKind: 'customer', subjectId: route.customerId, caseId, accountId };
  }
  if (route.businessId) {
    return { subjectKind: 'business', subjectId: route.businessId, caseId, accountId };
  }
  const subjectKind = params.get('subjectKind');
  const subjectId = params.get('subjectId');
  if (subjectKind === 'customer' || subjectKind === 'business') {
    return { subjectKind, subjectId, caseId, accountId };
  }
  return { subjectKind: null, subjectId, caseId, accountId };
}

export function useImpersonationSubjectParams(): {
  subjectKind: MirrorSubjectKind | null;
  subjectId: string | null;
  caseId: string | null;
  accountId: string | null;
} {
  return useMirrorSubjectParams();
}

export function getStoredCustomerId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(CUSTOMER_ID_STORAGE_KEY);
}

export function setStoredCustomerId(customerId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(CUSTOMER_ID_STORAGE_KEY, customerId);
}

export function getStoredBusinessId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(BUSINESS_ID_STORAGE_KEY);
}

export function setStoredBusinessId(businessId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(BUSINESS_ID_STORAGE_KEY, businessId);
}

export function getStoredImpersonationSession(): StoredImpersonationSession | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(IMPERSONATION_SESSION_STATE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as StoredImpersonationSession;
      if (parsed?.sessionId) return parsed;
    } catch {
      /* fall through to id-only key */
    }
  }
  const sessionId = localStorage.getItem(IMPERSONATION_SESSION_KEY);
  return sessionId ? { sessionId } : null;
}

export function getStoredImpersonationSessionId(): string | null {
  return getStoredImpersonationSession()?.sessionId ?? null;
}

export function getImpersonationRemainingSeconds(now = Date.now()): number {
  return remainingSeconds(getStoredImpersonationSession()?.expiresAt, now);
}

export function setStoredImpersonationSession(session: StoredImpersonationSession | null): void {
  if (typeof localStorage === 'undefined') return;
  if (!session?.sessionId) {
    localStorage.removeItem(IMPERSONATION_SESSION_KEY);
    localStorage.removeItem(IMPERSONATION_SESSION_STATE_KEY);
    return;
  }
  const next: StoredImpersonationSession = {
    ...session,
    expiresAt: session.expiresAt ?? defaultImpersonationExpiryIso(),
    startedAt: session.startedAt ?? new Date().toISOString(),
  };
  localStorage.setItem(IMPERSONATION_SESSION_KEY, next.sessionId);
  localStorage.setItem(IMPERSONATION_SESSION_STATE_KEY, JSON.stringify(next));
}

export function setStoredImpersonationSessionId(sessionId: string | null): void {
  if (!sessionId) {
    setStoredImpersonationSession(null);
    return;
  }
  const existing = getStoredImpersonationSession();
  setStoredImpersonationSession({
    ...(existing ?? {}),
    sessionId,
  });
}

export function getEndedImpersonationMarker(): EndedImpersonationMarker | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(IMPERSONATION_ENDED_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as EndedImpersonationMarker;
  } catch {
    return null;
  }
}

export function markImpersonationEnded(marker: EndedImpersonationMarker): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(IMPERSONATION_ENDED_KEY, JSON.stringify(marker));
}

export function clearEndedImpersonationMarker(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(IMPERSONATION_ENDED_KEY);
}

export function shouldBlockImpersonationReentry(
  subjectId?: string | null,
  caseId?: string | null,
): boolean {
  const ended = getEndedImpersonationMarker();
  if (!ended) return false;
  const active = getStoredImpersonationSessionId();
  if (!canReenterEndedImpersonation(ended.sessionId, active)) {
    if (subjectId && ended.subjectId && ended.subjectId !== subjectId) return false;
    if (caseId && ended.caseId && ended.caseId !== caseId) return false;
    return true;
  }
  return false;
}

/** Write session + A01 banner together. Do not render a second banner. */
export function persistActiveImpersonation(session: StoredImpersonationSession): void {
  setStoredImpersonationSession(session);
  const caseId = session.caseId ?? session.caseRef;
  setImpersonationBanner({
    intensity: session.writeEnabled ? 'impersonate-write' : 'impersonate-read',
    sessionId: session.sessionId,
    subjectName: session.subjectName ?? session.subjectId ?? 'Subject',
    subjectId: session.subjectId ?? session.sessionId,
    caseId,
    caseHref: caseId ? caseDetailPath(caseId) : caseReturnPath(caseId),
    expiresAt: session.expiresAt,
    consentAt: formatConsentClock(session.consentRecordedAt),
  });
}

export function clearActiveImpersonation(returnHref: string): void {
  setStoredImpersonationSession(null);
  setImpersonationBanner(null);
  setImpersonationEndedGuard(buildEndedGuard(returnHref));
  setSubjectHandoff(null);
}

/** In-memory only — ADR-009. Used to postMessage the subject app, never a URL. */
let subjectHandoffToken: string | null = null;
let subjectHandoffSurface: string | null = null;

export function setSubjectHandoff(token: string | null, surface?: string | null): void {
  subjectHandoffToken = token;
  subjectHandoffSurface = token ? (surface ?? null) : null;
}

export function getSubjectHandoff(): { accessToken: string; targetSurface: string | null } | null {
  if (!subjectHandoffToken) return null;
  return { accessToken: subjectHandoffToken, targetSurface: subjectHandoffSurface };
}

/** Opens the subject app and postMessages the impersonation token (never in the URL). */
export function openSubjectImpersonationApp(
  accessToken: string,
  targetSurface: string | null,
): boolean {
  const origin = subjectAppOrigin(targetSurface);
  if (!origin || typeof window === 'undefined') return false;
  const child = window.open(origin, 'beatq-impersonation');
  if (!child) return false;
  const onReady = (event: MessageEvent) => {
    if (!isImpersonationHandoffReadyMessage(event.data)) return;
    child.postMessage({ type: IMPERSONATION_HANDOFF_TOKEN, accessToken }, event.origin);
    window.removeEventListener('message', onReady);
  };
  window.addEventListener('message', onReady);
  return true;
}
